import React, { useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  Field,
  Spinner,
} from "@fluentui/react-components";
import { DeleteDismissFilled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { InvalidateOfferParams } from "../../service/api";

// Define the cancellation types from the XML structure
export const STORNO_TYPES = [
  { kod: "STR602", nazev: "Změna vlastníka" },
  { kod: "STR613", nazev: "Konec platnosti pojistného období" },
  { kod: "STR609", nazev: "Nesouhlas se změnou výše pojistného" },
  { kod: "STR611", nazev: "Do 2 měsíců od uzavření pojistné smlouvy" },
  { kod: "STR615", nazev: "Do 3 měsíců od vzniku pojistné události" },
  { kod: "STR642", nazev: "Zánik pojistného zájmu - POV + asistence" },
  { kod: "STR644", nazev: "Zánik pojistného zájmu - POV + další rizika" },
];

export interface StornoDialogProps {
  isOpen: boolean;
  offerId: string;
  onClose: () => void;
  onConfirm: (
    params: InvalidateOfferParams & { stornoType: string }
  ) => Promise<void>;
}

export const StornoDialog: React.FC<StornoDialogProps> = ({
  isOpen,
  offerId,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [stornoType, setStornoType] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    if (!stornoType) {
      setError(t("ila.storno.typeRequired"));
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      await onConfirm({
        offerId,
        stornoType,
      });
      onClose();
    } catch (error) {
      console.error("Error cancelling offer:", error);
      setError(t("ila.storno.error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, { open }) => !open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("ila.storno.title")}</DialogTitle>
          <DialogContent>
            <p>{t("ila.storno.confirmMessage", { offerId })}</p>
            <Field
              label={t("ila.storno.type")}
              required
              validationMessage={error || undefined}
            >
              <Select
                value={stornoType}
                onChange={(_, data) => setStornoType(data.value as string)}
              >
                <option value="" disabled selected hidden>
                  {t("ila.storno.selectType")}
                </option>
                {STORNO_TYPES.map((type) => (
                  <option key={type.kod} value={type.kod}>
                    {type.nazev}
                  </option>
                ))}
              </Select>
            </Field>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              {t("ila.cancel")}
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={isLoading}
              icon={
                isLoading ? (
                  <Spinner size="tiny" />
                ) : (
                  <DeleteDismissFilled fontSize={16} />
                )
              }
            >
              {t("ila.storno.confirm")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
